/* eslint-disable react/jsx-no-bind */
import type { MouseEventHandler } from "react";
import { useCallback, useState } from "react";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Collapse, IconButton, Link, Table, TableBody, TableCell, TableRow } from "@mui/material";
import { useTranslation } from "react-i18next";

import type { SystemTypeEnum } from "../../api/generated/graphql";
import {
  namedOperations,
  SystemGroupRoleEnum,
  useAddSystemFavoriteMutation,
  useDeleteSystemFavoriteMutation,
} from "../../api/generated/graphql";
import useNavigate, { routes } from "../../hooks/navigate/useNavigate";
import { useIsLoggedInUserSuperadmin } from "../../hooks/useLoggedInUser";
import FavoriteButton from "../shared/FavoriteButton/FavoriteButton";
import TemplateSystemChip from "../shared/TemplateSystem/TemplateSystemChip";

import type { OverviewSystem } from "./index";

type SystemRowProps = {
  onSystemClicked: (displayName: string) => void;
  system: OverviewSystem;
};

const SystemRowCollapsed = ({ system, onSystemClicked }: SystemRowProps) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation(["common", "SystemTypeEnum"]);
  const { navigateTo } = useNavigate();
  const isSuperadmin = useIsLoggedInUserSuperadmin();
  const [addSystem] = useAddSystemFavoriteMutation({
    refetchQueries: [namedOperations.Query.GetSystemsWithOperator],
  });
  const [deleteSystem] = useDeleteSystemFavoriteMutation({
    refetchQueries: [namedOperations.Query.GetSystemsWithOperator],
  });

  const toggleFavorite = useCallback(() => {
    const options = { variables: { input: { id: system.id } } };
    system.isFavorite ? void deleteSystem(options) : void addSystem(options);
  }, [system.id, system.isFavorite, addSystem, deleteSystem]);

  const isNoAccess = system.myRole === SystemGroupRoleEnum.NoAccess;
  const isRestricted = isNoAccess && !isSuperadmin;
  const systemLinkHrefValue = isRestricted ? "#" : routes.systems.show.with({ systemId: system.id });

  const handleSystemChange = useCallback<MouseEventHandler<HTMLAnchorElement>>(
    (event) => {
      event.preventDefault();
      if (isRestricted) {
        onSystemClicked(system.displayName);
        return;
      }

      navigateTo(routes.systems.show, { systemId: system.id });
    },
    [isRestricted, navigateTo, onSystemClicked, system.displayName, system.id],
  );

  const getSystemType = useCallback(
    (type: SystemTypeEnum | string | null | undefined): string => {
      if (type === null || type === undefined || type === "Unknown") return t("common:no_type");
      return t(`SystemTypeEnum:${type}`);
    },
    [t],
  );

  return (
    <>
      <TableRow>
        <TableCell align="center">
          {!isNoAccess && (
            <FavoriteButton
              enabled={Boolean(system.isFavorite)}
              onClick={() => toggleFavorite()}
              favoriteTitle={t("common:favorite_system")}
              unfavoriteTitle={t("common:unfavorite_system")}
              entityId={system.id}
            />
          )}
        </TableCell>
        <TableCell align="center">
          <Link href={systemLinkHrefValue} onClick={handleSystemChange}>
            {system.displayName}
          </Link>
          {system.isTemplate && <TemplateSystemChip />}
        </TableCell>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ padding: 0, margin: 0 }} colSpan={3}>
          <Collapse in={open} timeout="auto">
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell variant="head" align="center" sx={{ backgroundColor: "grey.100" }}>
                    {t("common:state_or_province")}
                  </TableCell>
                  <TableCell align="center">{system.addresses?.[0]?.region ?? ""}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell variant="head" align="center" sx={{ backgroundColor: "grey.100" }}>
                    {t("common:system_type")}
                  </TableCell>
                  <TableCell align="center">{getSystemType(system?.type)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell variant="head" align="center" sx={{ backgroundColor: "grey.100" }}>
                    {t("common:regulatory_id")}
                  </TableCell>
                  <TableCell align="center">{system.regulatoryId}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default SystemRowCollapsed;
